// src/components/weopen/WeOpenInboxPanel.tsx
//
// CRM WeOpen READ-ONLY surface (Phase 3 PR 2).
//
// A self-contained two-pane reader over the PR1 BFF. It shares no query, no
// cache tag and no component with the CRM thread list, which is the point: CRM
// threads come from CRM's database, these come live from WeOpen, and blending
// them would make it impossible to say which system a row's state belongs to.
//
// WHAT THIS IS NOT
//   - not in the All tab (a WeOpen-backed CommunicationThread from the legacy
//     webhook can already appear there; two sources for one conversation would
//     show it twice);
//   - no send box, no claim/release/return/close, no assignment control —
//     WeOpen owns the lifecycle and PR2 is read-only;
//   - no contact creation: opening a conversation resolves nothing and writes
//     nothing.
//
// THREE CONTRACT DETAILS THAT DRIVE THIS FILE
//
//   1. SCOPE IS EXPLICIT. WeOpen's inbox defaults to `filter=assigned`, so a
//      request without a filter hides every waiting handoff. The default view
//      here is therefore Waiting (`filter=unassigned`) — the queue is the thing
//      an agent most needs to see, and an inbox that quietly omitted it would be
//      worse than no inbox.
//   2. WAITING IS DERIVED, not reported. There is no `handoffState` field:
//      waiting means `status === 'human' && assignedAgentId === null`.
//   3. LIST AND DETAIL CARRY DIFFERENT IDENTITY. An inbox row has only
//      `externalUserId`; `crmContactId` exists on detail alone. And
//      `assignedAgentDisplayName` is the AGENT — using it as the customer label
//      would put a colleague's name on a stranger's conversation.

import { useState } from 'react';
import WeOpenContactContext from './WeOpenContactContext';
import WeOpenComposer from './WeOpenComposer';
import WeOpenOwnershipControls from './WeOpenOwnershipControls';
import {
  useGetWeOpenConversationsQuery,
  useGetWeOpenConversationQuery,
  useGetWeOpenConversationMessagesQuery,
  type WeOpenInboxItem,
  type WeOpenConversationDetail,
  type WeOpenMessage,
  type WeOpenInboxStatus,
  type WeOpenInboxFilter,
} from '../../api/weopenReadApi';

/** Bounded refresh for the list only. No sockets: PR2 adds no realtime bridge. */
// ── CONVERGENCE INTERVALS (Phase 4 PR 4) ────────────────────────────────────
//
// Before PR4 only the LIST polled. Detail and messages refetched solely on a local
// mutation, focus or reconnect — so a customer reply, or another agent claiming or
// closing the conversation an agent was reading, stayed invisible while the tab
// sat open and focused. That is the gap these two intervals close.
//
// Sockets would be the better mechanism and are NOT available: WeOpen's Socket.IO
// handshake verifies a symmetric secret and has no RS256/JWKS path, so a
// Central-mode agent token cannot authenticate. Activating it is a WeOpen change.
//
// Both detail queries are `skip`ped unless a conversation is selected, so these
// intervals only ever run for the ONE open conversation — never per row.
const LIST_POLL_MS = 15_000;
/** Matches the list: ownership and status move at human speed. */
const DETAIL_POLL_MS = 15_000;
/** Faster, because this is the only surface with a customer waiting on a reply. */
const MESSAGES_POLL_MS = 10_000;

/**
 * Scope first: it decides whether the queue is visible at all.
 *
 * LABELS STATE WHAT WEOPEN ACTUALLY RETURNS (Phase 3 PR4). Verified against
 * WeOpen's `agentInbox.controller` filter switch, not inferred from the names:
 *
 *   unassigned → assignedAgentId IS NULL AND status = HUMAN
 *                the unclaimed handoff queue, and nothing else.
 *   assigned   → assignedAgentId = me OR (assignedAgentId IS NULL AND
 *                status = HUMAN). So it is NOT "mine": it is mine PLUS the whole
 *                unclaimed queue. This tab was labelled "Mine", which overstated
 *                an agent's workload and made `Waiting` a strict SUBSET of it —
 *                the same conversation appeared under two tabs, one of which
 *                claimed the agent owned it. The label now says what arrives.
 *   monitoring → no assignedAgentId filter at all: every conversation visible
 *                under WeOpen's own scope rules. Not a curated monitor subset.
 *
 * The QUERY VALUES ARE UNCHANGED. CRM does not re-filter what WeOpen returns —
 * doing so would make CRM a second opinion on scope, which is precisely the
 * authority this integration keeps in WeOpen. If the semantics of `assigned`
 * ever need to change, that is a WeOpen change, not a CRM one.
 */
const SCOPE_TABS: Array<{ id: WeOpenInboxFilter; label: string }> = [
  { id: 'unassigned', label: 'Waiting' },
  { id: 'assigned', label: 'Assigned + Waiting' },
  { id: 'monitoring', label: 'Monitoring' },
];

const STATUS_TABS: Array<{ id: WeOpenInboxStatus; label: string }> = [
  { id: 'open', label: 'Open' },
  { id: 'closed', label: 'Closed' },
  { id: 'all', label: 'All' },
];

// ── Derived state ───────────────────────────────────────────────────────────

/**
 * Waiting = a human handoff nobody has claimed. Derived, because WeOpen reports
 * no handoff-state field.
 */
export function isWaiting(c: { status?: string | null; assignedAgentId?: string | null } | null | undefined): boolean {
  return !!c && c.status === 'human' && c.assignedAgentId === null;
}

/** A short label for the lifecycle, with waiting called out. */
export function statusLabel(
  c: { status?: string | null; assignedAgentId?: string | null } | null | undefined,
): string {
  if (!c?.status) return '';
  if (isWaiting(c)) return 'Waiting';
  if (c.status === 'human') return 'With agent';
  if (c.status === 'bot') return 'Bot';
  if (c.status === 'closed') return 'Closed';
  return c.status;
}

// ── Identity ────────────────────────────────────────────────────────────────

/**
 * LIST identity: `externalUserId` → 'Unknown'.
 *
 * An inbox row carries no `crmContactId` and no friendly visitor name, so there
 * is nothing else to show. Notably absent: `assignedAgentDisplayName`, which is
 * the agent.
 */
export function listDisplayName(c: WeOpenInboxItem | null | undefined): string {
  return c?.externalUserId || 'Unknown';
}

/**
 * DETAIL identity: `crmContactId` → `externalUserId` → 'Unknown'.
 *
 * The CRM contact id is shown as the link it is, not resolved — resolving it
 * here would mean a contact lookup as a side effect of reading, and PR2 writes
 * and fetches nothing outside the three BFF reads.
 */
export function detailDisplayName(c: WeOpenConversationDetail | null | undefined): string {
  if (!c) return 'Unknown';
  if (c.crmContactId) return `CRM contact ${c.crmContactId}`;
  return c.externalUserId || 'Unknown';
}

// ── Error semantics ─────────────────────────────────────────────────────────

interface ViewError {
  title: string;
  detail: string;
  retryable: boolean;
}

function httpStatusOf(err: unknown): number | undefined {
  const s = (err as { status?: unknown })?.status;
  return typeof s === 'number' ? s : undefined;
}

function codeOf(err: unknown): string | undefined {
  const c = (err as { data?: { error?: unknown } })?.data?.error;
  return typeof c === 'string' ? c : undefined;
}

/**
 * Read endpoints return 400/401/403/404/503 from WeOpen, plus 502/504 when the
 * BFF cannot reach it. 409 and 422 are NOT part of the read contract and are
 * deliberately not special-cased — inventing handling for codes that never
 * arrive would be fiction.
 */
export function describeError(err: unknown): ViewError {
  const status = httpStatusOf(err);
  const code = codeOf(err);

  if (code === 'weopen_url_not_configured') {
    return {
      title: 'WeOpen is not configured',
      detail: 'This environment has no WeOpen address set. Ask an administrator to configure it.',
      retryable: false,
    };
  }
  switch (status) {
    case 400:
      return {
        title: 'That request was rejected',
        detail:
          code === 'invalid_filter' || code === 'invalid_status'
            ? 'The selected view is not one WeOpen accepts.'
            : 'WeOpen or the CRM rejected the request as invalid.',
        retryable: false,
      };
    case 401:
      return {
        title: 'Your session expired',
        detail: 'Sign in again to read WeOpen conversations.',
        retryable: false,
      };
    case 403:
      return {
        title: 'Not permitted',
        detail: 'WeOpen has not granted you access to this. Nothing is hidden by the CRM.',
        retryable: false,
      };
    case 404:
      return {
        title: 'Conversation unavailable',
        detail: 'It may have been closed, reassigned, or is outside what you can see.',
        retryable: false,
      };
    case 503:
      return {
        title: 'WeOpen is unavailable',
        detail: 'This is an upstream outage, not an empty inbox. Your conversations are unaffected.',
        retryable: true,
      };
    case 502:
    case 504:
      return {
        title: 'Could not reach WeOpen',
        detail: status === 504 ? 'The request timed out.' : 'The connection failed.',
        retryable: true,
      };
    default:
      return {
        title: 'Could not load from WeOpen',
        detail: 'Something went wrong upstream.',
        retryable: true,
      };
  }
}

function ErrorState({ err, onRetry, testId }: { err: unknown; onRetry?: () => void; testId: string }) {
  const { title, detail, retryable } = describeError(err);
  return (
    <div
      data-testid={testId}
      role="alert"
      className="m-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm"
    >
      <p className="font-medium text-amber-900">{title}</p>
      <p className="mt-1 text-amber-800">{detail}</p>
      {retryable && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          data-testid={`${testId}-retry`}
          className="mt-3 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100"
        >
          Try again
        </button>
      )}
    </div>
  );
}

// ── Messages ────────────────────────────────────────────────────────────────

/**
 * A system notice is identified by `role === 'system'`. `metadata.isSystemMessage`
 * is tolerated as a hint when present but never required — a notice that arrived
 * without it would otherwise be rendered as if a person had said it.
 */
export function isSystemNotice(m: WeOpenMessage): boolean {
  if (m.role === 'system') return true;
  const flag = (m.metadata as Record<string, unknown> | null | undefined)?.isSystemMessage;
  return flag === true;
}

/** Customer side. `senderType: 'user'` is the customer; bot and agent are ours. */
export function isFromCustomer(m: WeOpenMessage): boolean {
  return m.senderType === 'user';
}

function timeOf(v?: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

// ── Panel ───────────────────────────────────────────────────────────────────

export default function WeOpenInboxPanel() {
  // Waiting first, deliberately — see the header note on WeOpen's default.
  const [scope, setScope] = useState<WeOpenInboxFilter>('unassigned');
  const [status, setStatus] = useState<WeOpenInboxStatus>('open');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useGetWeOpenConversationsQuery(
    { status, filter: scope },
    { pollingInterval: LIST_POLL_MS },
  );
  const detail = useGetWeOpenConversationQuery(selectedId as string, {
    skip: !selectedId,
    pollingInterval: DETAIL_POLL_MS,
  });
  const messages = useGetWeOpenConversationMessagesQuery(selectedId as string, {
    skip: !selectedId,
    pollingInterval: MESSAGES_POLL_MS,
  });

  const conversations = list.data ?? [];

  return (
    <div className="flex h-[calc(100vh-8rem)]" data-testid="weopen-panel">
      {/* ── List ── */}
      <div className="flex w-96 flex-col border-r border-gray-200 bg-white">
        <div className="space-y-3 border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">WeOpen conversations</h2>
            <button
              type="button"
              onClick={() => void list.refetch()}
              data-testid="weopen-refresh"
              className="text-xs font-medium text-blue-600 hover:text-blue-700"
            >
              Refresh
            </button>
          </div>

          {/* Scope — the parameter that decides whether the queue is visible. */}
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1" role="tablist" aria-label="Scope">
            {SCOPE_TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={scope === t.id}
                onClick={() => {
                  setScope(t.id);
                  setSelectedId(null);
                }}
                data-testid={`weopen-scope-${t.id}`}
                className={`flex-1 rounded-md px-2 py-1.5 text-sm font-medium transition-colors ${
                  scope === t.id ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Lifecycle. These two are the only filters the contract supports. */}
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1" role="tablist" aria-label="Status">
            {STATUS_TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={status === t.id}
                onClick={() => {
                  setStatus(t.id);
                  setSelectedId(null);
                }}
                data-testid={`weopen-status-${t.id}`}
                className={`flex-1 rounded-md px-2 py-1.5 text-sm font-medium transition-colors ${
                  status === t.id ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {list.isLoading ? (
            <div data-testid="weopen-list-loading" className="space-y-3 p-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded-lg bg-gray-100" />
              ))}
            </div>
          ) : list.isError ? (
            // An upstream failure is NEVER an empty inbox.
            <ErrorState err={list.error} onRetry={() => void list.refetch()} testId="weopen-list-error" />
          ) : conversations.length === 0 ? (
            <p data-testid="weopen-list-empty" className="p-6 text-center text-sm text-gray-500">
              No {scope === 'unassigned' ? 'waiting' : scope === 'assigned' ? 'assigned' : 'monitored'}{' '}
              WeOpen conversations.
            </p>
          ) : (
            <ul data-testid="weopen-list">
              {conversations.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    data-testid={`weopen-row-${c.id}`}
                    aria-current={selectedId === c.id}
                    className={`w-full border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-50 ${
                      selectedId === c.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      {/* externalUserId → Unknown. Never the agent's name. */}
                      <span className="truncate text-sm font-medium text-gray-900">
                        {listDisplayName(c)}
                      </span>
                      {c.status && (
                        <span
                          data-testid={`weopen-row-status-${c.id}`}
                          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
                            isWaiting(c)
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-purple-50 text-purple-700'
                          }`}
                        >
                          {statusLabel(c)}
                        </span>
                      )}
                    </div>
                    {c.lastMessagePreview && (
                      <p className="mt-0.5 truncate text-xs text-gray-500">{c.lastMessagePreview}</p>
                    )}
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-400">
                      {c.lastMessageAt && <span>{timeOf(c.lastMessageAt)}</span>}
                      {c.botName && <span>· {c.botName}</span>}
                      {/* Clearly attributed as the agent, never as the customer. */}
                      {c.assignedAgentDisplayName && (
                        <span data-testid={`weopen-row-agent-${c.id}`}>
                          · Agent: {c.assignedAgentDisplayName}
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ── Detail ── */}
      <div className="flex flex-1 flex-col bg-white">
        {!selectedId ? (
          <p data-testid="weopen-no-selection" className="m-auto text-sm text-gray-500">
            Select a conversation to read it.
          </p>
        ) : (
          <>
            <div className="border-b border-gray-200 p-4">
              {detail.isLoading ? (
                <div data-testid="weopen-detail-loading" className="h-6 w-48 animate-pulse rounded bg-gray-100" />
              ) : detail.isError ? (
                <ErrorState err={detail.error} testId="weopen-detail-error" />
              ) : (
                <div data-testid="weopen-detail">
                  {/* crmContactId → externalUserId → Unknown */}
                  <h3 className="text-base font-semibold text-gray-900">
                    {detailDisplayName(detail.data)}
                  </h3>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    {detail.data?.status && (
                      <span data-testid="weopen-detail-status">{statusLabel(detail.data)}</span>
                    )}
                    {detail.data?.assignedAgent?.name && (
                      <span data-testid="weopen-detail-assignee">
                        · Agent: {detail.data.assignedAgent.name}
                      </span>
                    )}
                    {detail.data?.handoffReason && (
                      <span data-testid="weopen-detail-handoff-reason">
                        · {detail.data.handoffReason}
                      </span>
                    )}
                    {detail.data?.bot?.name && <span>· {detail.data.bot.name}</span>}
                  </div>
                  {/* Phase 4 PR1 made replying possible, so the old
                      "read-only" wording became untrue. The lifecycle is still
                      WeOpen's: claim, release, return and close are not here. */}
                  {/* Narrowed again by PR3, which added close. What is still not
                      here: reassigning (no authoritative WeOpen endpoint) and
                      reopening (WeOpen has none — close is irreversible). */}
                  <p className="mt-2 text-xs text-gray-400">
                    You can claim, reply, release and close here. Reassigning is managed in WeOpen.
                  </p>

                  {/* Explicit ownership actions. Never automatic: PR1 exists so that
                      replying cannot acquire ownership, and an auto-claim would
                      reintroduce that. On a confirmed change the list, detail and
                      transcript are refetched — WeOpen's state is the real one. */}
                  <WeOpenOwnershipControls
                    conversationId={selectedId}
                    conversation={detail.data}
                    onOwnershipChanged={() => {
                      void list.refetch();
                      void detail.refetch();
                      void messages.refetch();
                    }}
                  />

                  {/* CRM contact context (PR3).
                      Heading above stays WeOpen's identity; THIS is CRM's, and
                      the two authorities stay visibly separate. Renders nothing
                      when WeOpen reports no crmContactId, and issues no request
                      in that case. Its failures are local: the conversation and
                      its messages remain readable. */}
                  <WeOpenContactContext crmContactId={detail.data?.crmContactId ?? null} />
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {messages.isLoading ? (
                <div data-testid="weopen-messages-loading" className="space-y-3">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-10 animate-pulse rounded-lg bg-gray-100" />
                  ))}
                </div>
              ) : messages.isError ? (
                <ErrorState
                  err={messages.error}
                  onRetry={() => void messages.refetch()}
                  testId="weopen-messages-error"
                />
              ) : (messages.data ?? []).length === 0 ? (
                <p data-testid="weopen-messages-empty" className="text-center text-sm text-gray-500">
                  No messages in this conversation.
                </p>
              ) : (
                <ul data-testid="weopen-messages" className="space-y-3">
                  {(messages.data ?? []).map((m) => {
                    // System notices are neither side's speech — centred, plain.
                    if (isSystemNotice(m)) {
                      return (
                        <li key={m.id} data-testid={`weopen-message-${m.id}`} className="flex justify-center">
                          <p
                            data-testid={`weopen-system-${m.id}`}
                            className="max-w-[80%] text-center text-xs italic text-gray-500"
                          >
                            {m.content}
                          </p>
                        </li>
                      );
                    }
                    const customer = isFromCustomer(m);
                    return (
                      <li
                        key={m.id}
                        data-testid={`weopen-message-${m.id}`}
                        className={`flex ${customer ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg px-3 py-2 text-sm ${
                            customer ? 'bg-gray-100 text-gray-900' : 'bg-blue-600 text-white'
                          }`}
                        >
                          <p className="whitespace-pre-wrap break-words">{m.content}</p>
                          <p className={`mt-1 text-xs ${customer ? 'text-gray-500' : 'text-blue-100'}`}>
                            {m.senderType === 'bot' ? 'Bot · ' : m.senderType === 'agent' ? 'Agent · ' : ''}
                            {timeOf(m.createdAt)}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Agent reply composer (Phase 4 PR1).
                Sits BELOW the transcript, outside the scroll area, so it stays
                reachable. WeOpen authorizes every send; on a confirmed send the
                transcript is refetched, because WeOpen's row is the real one and
                PR1 adds no realtime. */}
            <WeOpenComposer
              conversationId={selectedId}
              conversation={detail.data}
              onSent={() => void messages.refetch()}
            />
          </>
        )}
      </div>
    </div>
  );
}
